import { useState } from "react";
import type { Vendor } from "../../lib/vendors/types";
import { logoUrlForVendor } from "../../lib/vendors/logos";

// Wave 9.5 — VendorLogo. Renders either a Clearbit <img> (uploaded URL
// takes priority, see logoUrlForVendor) or a deterministic initial-letter
// avatar. The initial + color are derived from `vendor.normalizedName` so
// the same vendor always lands in the same palette slot across pages.
//
// The img gets `referrerPolicy="no-referrer"` so Clearbit doesn't see the
// caller URL, and `loading="lazy"` so long vendor lists don't fire a
// thundering herd of logo requests. On any img error event we flip to the
// fallback avatar — covers 404s (not in the mapped-domain set) and network
// failures alike.

export type VendorLogoSize = 20 | 24 | 32 | 48;

interface VendorLogoProps {
  vendor: Vendor;
  size?: VendorLogoSize;
  className?: string;
}

// Tailwind palette for fallback avatars. Six-entry cycle — keeps the look
// consistent without pulling in a huge color dep.
const PALETTE: ReadonlyArray<string> = [
  "bg-indigo-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-fuchsia-500",
];

// Tiny stable string hash. djb2-ish — good enough for deterministic color
// assignment, not for security. We only need the low bits.
function stringHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  // Force positive.
  return h < 0 ? ~h : h;
}

function initialFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  // First character of the first non-empty word. Uppercased — fallback avatar
  // is always a single capital letter.
  const firstWord = trimmed.split(/\s+/)[0] ?? trimmed;
  const codePoint = firstWord[0] ?? "?";
  return codePoint.toUpperCase();
}

// Map size → Tailwind size + text classes. Using explicit tokens so Tailwind
// JIT sees literal class names at build time (runtime string concatenation
// would get purged).
const SIZE_CLASSES: Record<
  VendorLogoSize,
  { box: string; text: string; pixels: number }
> = {
  20: { box: "w-5 h-5", text: "text-[10px]", pixels: 20 },
  24: { box: "w-6 h-6", text: "text-xs", pixels: 24 },
  32: { box: "w-8 h-8", text: "text-sm", pixels: 32 },
  48: { box: "w-12 h-12", text: "text-lg", pixels: 48 },
};

export function VendorLogo({
  vendor,
  size = 24,
  className = "",
}: VendorLogoProps) {
  const [errored, setErrored] = useState(false);
  const src = logoUrlForVendor(vendor);
  const dims = SIZE_CLASSES[size];

  const showImg = src != null && !errored;

  if (showImg) {
    return (
      <img
        data-testid={`vendor-logo-${vendor.id}`}
        src={src!}
        // Decorative: every place we render <VendorLogo /> already has the
        // vendor's canonical name as text right next to it, so exposing the
        // logo to AT as a second "X logo" label just muddies the reading
        // order. Empty alt + aria-hidden hide the logo from the accessible
        // name of the parent (matters for e.g. `getByRole("link", {name})`).
        alt=""
        aria-hidden="true"
        loading="lazy"
        referrerPolicy="no-referrer"
        width={dims.pixels}
        height={dims.pixels}
        onError={() => setErrored(true)}
        className={`${dims.box} rounded object-contain bg-white border border-slate-200 ${className}`}
      />
    );
  }

  // Fallback: initial-letter avatar. Fires when there's no mapped domain, no
  // uploaded override, or when the Clearbit <img> errored out.
  const color = PALETTE[stringHash(vendor.normalizedName || vendor.canonicalName) % PALETTE.length];
  return (
    <span
      data-testid={`vendor-logo-fallback-${vendor.id}`}
      aria-hidden="true"
      className={`inline-flex items-center justify-center shrink-0 rounded text-white font-semibold select-none ${dims.box} ${dims.text} ${color} ${className}`}
    >
      {initialFor(vendor.canonicalName)}
    </span>
  );
}
